<template>
	<base-modal
		ref="$modal"
		fullscreen-on-mobile
		:title="$t('notifications_title')"
		width="600px"
		height="80vh"
		@on-close="onClose"
	>
		<div
			ref="$content"
			class="notifications-modal"
		>
			<div class="notifications-modal__tabs">
				<button
					type="button"
					class="notifications-modal__tab"
					:class="{ 'notifications-modal__tab--active': tab === 'active' }"
					:disabled="moveAllPending"
					@click="setTab('active')"
				>
					{{ $t('notifications_new') }}
					<span class="notifications-modal__tab-counter">
						{{ activeCount }}
					</span>
				</button>
				<button
					type="button"
					class="notifications-modal__tab"
					:class="{ 'notifications-modal__tab--active': tab === 'history' }"
					:disabled="moveAllPending"
					@click="setTab('history')"
				>
					{{ $t('notifications_history') }}
				</button>

				<app-resizable-dropdown
					v-if="canMoveAll"
					ref="$moveAllConfirm"
					class="notifications-modal__move-all-wrap"
					position="bottom"
					:max-width="280"
					:content-z-index="10002"
					close-by-click-outside
					centered-on-mobile
				>
					<template #activator>
						<button
							type="button"
							class="notifications-modal__move-all"
						>
							{{ $t('notifications_move_all') }}
							<app-icon
								name="rollback"
								size="18px"
								class="notifications-modal__move-all-icon"
								:style="{ marginTop: '-2px' }"
							/>
						</button>
					</template>
					<notification-move-all-confirm
						@confirm="onConfirmMoveAll"
						@cancel="$moveAllConfirm?.close()"
					/>
				</app-resizable-dropdown>
			</div>

			<div class="notifications-modal__body">
				<div class="notifications-modal__list">
					<app-loader v-if="loading" />
					<template v-else-if="currentList.length">
						<template v-for="(n, index) in currentList" :key="n.Id">
							<notification-item
								:ref="setItemRef"
								:notification="n"
								:show-move-action="tab === 'active'"
								@move-to-history="onMoveToHistory"
							/>
							<div
								v-if="index + 1 < currentList.length"
								class="notifications-modal__divider"
							/>
						</template>
					</template>
					<div v-else class="notifications-modal__empty">
						{{ $t('list_empty') }}
					</div>
				</div>

				<notification-undo-overlay
					v-if="moveAllPending"
					ref="$moveAllUndo"
					:style="{ zIndex: 4 }"
					:text="$t('notifications_moved_to_history')"
					@finish="onMoveAllFinish"
					@cancel="onCancelMoveAll"
				/>
			</div>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import { computed, onBeforeUpdate, ref } from "vue"
import BaseModal from "@/components/common/modal/BaseModal.vue"
import AppLoader from "@/components/common/AppLoader.vue"
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue"
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue"
import NotificationItem from "./NotificationItem.vue"
import NotificationMoveAllConfirm from "./NotificationMoveAllConfirm.vue"
import NotificationUndoOverlay from "./NotificationUndoOverlay.vue"
import { headerStore } from "@/store/header"
import { useModalEscape } from "@/composables/useModalEscape"

const store = headerStore()
const $content = ref<HTMLElement | null>(null)
const $modal = ref<typeof BaseModal | null>(null)
const $moveAllConfirm = ref<typeof AppResizableDropdown | null>(null)
const $moveAllUndo = ref<InstanceType<typeof NotificationUndoOverlay> | null>(null)
const tab = ref<'active' | 'history'>('active')
const loading = ref(false)
const moveAllPending = ref(false)

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

let itemRefs: any[] = []

onBeforeUpdate(() => {
	itemRefs = []
})

const setItemRef = (el: any) => {
	if (el) {
		itemRefs.push(el)
	}
}

const currentList = computed(() =>
	tab.value === 'active' ? store.activeNotifications : store.historyNotifications
)

const activeCount = computed(() => store.notificationCount.JobCount)

const canMoveAll = computed(() =>
	tab.value === 'active'
	&& store.activeNotifications.length > 0
	&& !moveAllPending.value
)

const loadCurrentTab = async () => {
	loading.value = true
	try {
		if (tab.value === 'active') {
			await store.loadActiveNotifications(0)
		} else {
			await store.loadHistoryNotifications(0)
		}
	} finally {
		loading.value = false
	}
}

const setTab = async (value: 'active' | 'history') => {
	tab.value = value
	await loadCurrentTab()
}

const onMoveToHistory = async (id: number) => {
	await store.moveNotificationToHistory(id)
}

const onConfirmMoveAll = () => {
	$moveAllConfirm.value?.close()
	moveAllPending.value = true
}

const onCancelMoveAll = () => {
	moveAllPending.value = false
}

const onMoveAllFinish = async () => {
	moveAllPending.value = false
	loading.value = true
	try {
		await store.moveAllToHistory()
	} finally {
		loading.value = false
	}
}

const open = async () => {
	moveAllPending.value = false
	$modal.value?.onOpen()
	activateEsc()
	tab.value = 'active'
	await loadCurrentTab()
}

const onClose = () => {
	deactivateEsc()
	if (moveAllPending.value) {
		$moveAllUndo.value?.flush()
		return
	}
	itemRefs.forEach(item => item?.flushPendingMove?.())
}

defineExpose({ open })
</script>

<style scoped lang="scss">
.notifications-modal {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;

	&__tabs {
		display: flex;
		gap: 16px;
		padding: 0 12px;
		border-bottom: 1px solid #ECECEC;
		margin-bottom: 8px;
	}

	&__tab {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		background: transparent;
		border: none;
		padding: 8px 0;
		font-size: 14px;
		line-height: 18px;
		font-weight: 500;
		cursor: pointer;
		color: #979797;
		border-bottom: 3px solid transparent;
		margin-bottom: -1px;
		font-family: inherit;

		&:disabled {
			cursor: default;
		}

		&--active {
			color: #3F884F;
			border-bottom-color: #3F884F;

			.notifications-modal__tab-counter {
				color: #3F884F;
			}
		}
	}

	&__tab-counter {
		padding: 1px 5px;
		border-radius: 8px;
		font-size: 12px;
		line-height: 16px;
		color: #979797;
		background: #F2F2F2;
	}

	&__move-all-wrap {
		margin-left: auto;
		margin-right: 2px;
		align-self: center;
	}

	&__move-all {
		display: inline-flex;
		align-items: center;
		background: transparent;
		border: none;
		padding: 0;
		cursor: pointer;
		font-family: inherit;
		font-size: 14px;
		font-weight: 500;
		color: #979797;
		transition: color 0.15s ease;

		&:hover {
			color: #3F884F;
		}
	}

	&__move-all-icon {
		margin-left: 8px;
	}

	&__body {
		position: relative;
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	&__list {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 0;
		padding: 4px 4px 8px 4px;
	}

	&__divider {
		width: 100%;
		border-top: 1px solid #F2F2F2;
		margin: 8px 0;
	}

	&__empty {
		padding: 32px;
		text-align: center;
		color: #979797;
	}
}
</style>
